/** Exact fixed-scale decimal arithmetic. No IEEE-754 financial intermediates. */
export class Decimal {
    units;
    scale;
    constructor(value, scale = 0) {
        if (typeof value === "bigint") {
            this.units = value;
            this.scale = scale;
            return;
        }
        if (value.length > 160 || !/^-?\d+(\.\d+)?$/.test(value))
            throw new Error("Invalid decimal");
        const [whole = "0", fraction = ""] = value.split(".");
        this.scale = fraction.length;
        this.units = BigInt(whole + fraction);
    }
    add(other) {
        const s = Math.max(this.scale, other.scale);
        return new Decimal(this.units * 10n ** BigInt(s - this.scale) + other.units * 10n ** BigInt(s - other.scale), s);
    }
    sub(other) {
        return this.add(new Decimal(-other.units, other.scale));
    }
    mul(other) {
        return new Decimal(this.units * other.units, this.scale + other.scale);
    }
    div(other, precision = 24) {
        if (other.units === 0n || precision < 0 || precision > 80)
            throw new Error("Invalid division");
        return new Decimal((this.units * 10n ** BigInt(other.scale + precision)) /
            (other.units * 10n ** BigInt(this.scale)), precision);
    }
    compare(other) {
        const n = this.sub(other).units;
        return n < 0n ? -1 : n > 0n ? 1 : 0;
    }
    toString() {
        const neg = this.units < 0n;
        const digits = (neg ? -this.units : this.units).toString().padStart(this.scale + 1, "0");
        const result = this.scale
            ? `${digits.slice(0, -this.scale)}.${digits.slice(-this.scale)}`.replace(/\.?0+$/, "")
            : digits;
        return `${neg && this.units !== 0n ? "-" : ""}${result}`;
    }
}
export const decimal = (value) => new Decimal(value);
/** Fraction digits every cached USD amount is truncated to, so sums never widen without bound. */
export const MONEY_SCALE = 18;
/** Truncates toward zero on the bigint units; a string round-trip would re-parse and lose the scale. */
export function truncate(value, scale) {
    if (!Number.isInteger(scale) || scale < 0 || scale > 80)
        throw new Error("Invalid scale");
    if (value.scale <= scale)
        return value;
    return new Decimal(value.units / 10n ** BigInt(value.scale - scale), scale);
}
export function nativeUnits(value, decimals) {
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18)
        throw new Error("Invalid token decimals");
    const d = decimal(value);
    if (d.units < 0n)
        throw new Error("Negative amount");
    if (d.scale <= decimals)
        return d.units * 10n ** BigInt(decimals - d.scale);
    const factor = 10n ** BigInt(d.scale - decimals);
    if (d.units % factor !== 0n)
        throw new Error("Amount exceeds token precision");
    return d.units / factor;
}
export function formatNative(value, decimals) {
    return new Decimal(value, decimals).toString();
}
export function unsignedNative(value) {
    if (!/^(0|[1-9]\d{0,77})$/.test(value))
        throw new Error("Invalid native amount");
    return BigInt(value);
}
/** SDK exact-input minimum, anchored to the original expected output. */
export function slippageMinimum(expected, bps) {
    if (typeof expected !== "bigint" ||
        expected < 0n ||
        expected >= 1n << 64n ||
        !Number.isInteger(bps) ||
        bps < 0 ||
        bps > 10000)
        throw new Error("Invalid slippage inputs");
    return (expected * (10000n - BigInt(bps))) / 10000n;
}
/** Inverts the floored minimum exactly; the return value is basis points, not native units. */
export function requiredSlippage(expected, current) {
    slippageMinimum(expected, 0);
    slippageMinimum(current, 0);
    if (expected === 0n || current === 0n)
        return null;
    const numerator = 10000n * (expected - current - 1n) + 1n;
    const required = numerator <= 0n ? 0n : (numerator + expected - 1n) / expected;
    // Bounded enumeration avoids converting native arithmetic through Number.
    for (let bps = 0; bps <= 10000; bps++)
        if (BigInt(bps) === required)
            return bps;
    throw new Error("Invalid slippage result");
}
export function invertPrice(value) {
    if (decimal(value).units <= 0n)
        throw new Error("Price must be positive");
    return decimal("1").div(decimal(value)).toString();
}
/** On-chain sqrtPriceX64 to human B/A price, adjusting mint decimals exactly. */
export function sqrtPriceToPrice(sqrtPriceX64, decimalsA, decimalsB) {
    if (sqrtPriceX64 <= 0n ||
        ![decimalsA, decimalsB].every((d) => Number.isInteger(d) && d >= 0 && d <= 18))
        throw new Error("Invalid price inputs");
    return new Decimal(sqrtPriceX64 * sqrtPriceX64 * 10n ** BigInt(decimalsA))
        .div(new Decimal((1n << 128n) * 10n ** BigInt(decimalsB)))
        .toString();
}
/** Percent multiplication terminates exactly, including sub-24-decimal token prices. */
export function percentOf(value, percentage) {
    const product = decimal(value).mul(decimal(percentage));
    return new Decimal(product.units, product.scale + 2).toString();
}
